import type { VoidFunctionComponent } from 'react';

import { isFreeTier } from '../../../../../shared/subscriptions';
import { window } from '../../../../globals';
import { Icon, icons } from '../../../generic';
import Button from '../../../generic/Button';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import { ExportIssuesModalDetailsContainer } from '../../modals';
import { useIssueDetailsModals } from '../IssueDetailsModalsContext';
import type { Issue } from '../../../../../shared/issuesV2';

import { SELECTED_ISSUE_COUNT } from './shared';
import type { ActionButtonAndModalProps } from './shared';

type Props = ActionButtonAndModalProps & {
  issue: Issue;
  projectId?: string;
  //  The count of number of unique Jira tickets which currenty exist on this issue.
  //  This will only be set for the global scope issue details.
  jiraTicketCount?: number;
};

/**
 * IssueDetailsActionButtonExport includes the button and the modal
 * for the export action on the issue details page and drawer. The modal state is managed
 * by useIssueDetailsModals, which passes the modal state down as context. Go see the
 * IssueDetailsModalsContext for more info.
 */
const IssueDetailsActionButtonExport: VoidFunctionComponent<Props> = ({
  id,
  issue,
  projectId,
  jiraTicketCount = 0,
}) => {
  const { exportModal, isAnyModalOpen } = useIssueDetailsModals();

  const isOrgOnFreeTier = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });

  return (
    <>
      <PaidFeatureGate isRestricted={isOrgOnFreeTier} hidePill>
        <Button
          type="secondary"
          size="medium"
          onClick={exportModal.onOpen}
          isDisabled={isAnyModalOpen}
        >
          Create ticket
          {' '}{isOrgOnFreeTier && <Icon icon={icons.lockOutline} />}
        </Button>
      </PaidFeatureGate>
      <ExportIssuesModalDetailsContainer
        id={id}
        show={exportModal.isOpen}
        issue={issue}
        onHide={exportModal.onClose}
        projectId={projectId}
        selectedIssuesCount={SELECTED_ISSUE_COUNT}
        jiraTicketCount={jiraTicketCount}
      />
    </>
  );
};

export default IssueDetailsActionButtonExport;
